import {
  Activity,
  BadgeDollarSign,
  BarChart3,
  Blocks,
  Building2,
  Cpu,
  KeyRound,
  KeySquare,
  MessageSquareText,
  ReceiptText,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  UsersRound,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ListNavGroup } from "@/components/ListNav";
import { type Capabilities, useCapabilities } from "@/features/account/useCurrentUser";

/** Capability each gated section needs; also read by RequireCapability on its route. */
export const SECTION_CAPABILITY = {
  governance: "azureAdminEnabled",
  platformCosts: "azureAdminEnabled",
  oauthClients: "oauthClientManagementEnabled",
  authorizations: "automatedSyncEnabled",
} as const satisfies Record<string, keyof Capabilities>;

/**
 * The Admin nav (docs/design/web/WEB_ADMIN_DESIGN.md "Information architecture"). The
 * ONE place admin sections are gated: capability-dependent items set
 * `hidden` from `useCapabilities()` here, never inside pages.
 */
export function useAdminNav(): ListNavGroup[] {
  const { t } = useTranslation("admin");
  const caps = useCapabilities();
  const off = (section: keyof typeof SECTION_CAPABILITY) => !caps[SECTION_CAPABILITY[section]];
  return [
    {
      id: "organization",
      label: t("nav.groups.organization"),
      items: [
        { to: "/admin/users", label: t("nav.users"), icon: <Users /> },
        { to: "/admin/company", label: t("nav.company"), icon: <Building2 /> },
        { to: "/admin/feedback", label: t("nav.feedback"), icon: <MessageSquareText /> },
      ],
    },
    {
      id: "ai",
      label: t("nav.groups.ai"),
      items: [
        { to: "/admin/models", label: t("nav.models"), icon: <Cpu /> },
        { to: "/admin/model-defaults", label: t("nav.modelDefaults"), icon: <SlidersHorizontal /> },
        { to: "/admin/integrations", label: t("nav.integrations"), icon: <Blocks /> },
        { to: "/admin/governance", label: t("nav.governance"), icon: <ShieldCheck />, hidden: off("governance") },
        { to: "/admin/content-safety", label: t("nav.contentSafety"), icon: <ShieldAlert /> },
      ],
    },
    {
      id: "usage",
      label: t("nav.groups.usage"),
      items: [
        { to: "/admin/usage", label: t("nav.usageOverview"), icon: <BarChart3 />, end: true },
        { to: "/admin/usage/users", label: t("nav.usageByUser"), icon: <UsersRound /> },
        { to: "/admin/usage/budgets", label: t("nav.budgets"), icon: <BadgeDollarSign /> },
        {
          to: "/admin/usage/costs",
          label: t("nav.platformCosts"),
          icon: <ReceiptText />,
          hidden: off("platformCosts"),
        },
      ],
    },
    {
      id: "platform",
      label: t("nav.groups.platform"),
      items: [
        { to: "/admin/license", label: t("nav.license"), icon: <KeyRound /> },
        { to: "/admin/oauth-clients", label: t("nav.oauthClients"), icon: <KeySquare />, hidden: off("oauthClients") },
        {
          to: "/admin/authorizations",
          label: t("nav.authorizations"),
          icon: <Activity />,
          hidden: off("authorizations"),
        },
      ],
    },
  ];
}
